import { TalkFile, TalkVideo, useGetTalksMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type Consultable<T> = T & {
    consulted: boolean;
};

export const useCauseriesTalkFilesPageVM = () => {
    const { thematicId, talkId } = useParams();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');

    const [files, setFiles] = useState<Consultable<TalkFile>[]>([]);
    const [videos, setVideos] = useState<Consultable<TalkVideo>[]>([]);

    const [getTalks, { isLoading: isLoadingTalks, data: talks, error: errorTalks }] = useGetTalksMutation();

    const acknowledgeFile = (file: TalkFile) => {
        setFiles(files.map((f) => (f.id === file.id ? { ...f, consulted: true } : f)));
    };

    const acknowledgeVideo = (video: TalkVideo) => {
        setVideos(videos.map((v) => (v.id === video.id ? { ...v, consulted: true } : v)));
    };

    const areFilesAcknowledged = useMemo(() => files.every((file) => file.consulted), [files]);
    const areVideosAcknowledged = useMemo(() => videos.every((video) => video.consulted), [videos]);

    const isEveryAssetAcknowledged = useMemo(() => {
        if (isLoadingTalks) {
            return false;
        }
        if (!files.length && !videos.length) {
            return false;
        }
        return areFilesAcknowledged && areVideosAcknowledged;
    }, [areFilesAcknowledged, areVideosAcknowledged, isLoadingTalks, files, videos]);

    useEffect(() => {
        if (!talkId || !thematicId) return;
        getTalks({ thematicId: parseInt(thematicId) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [talkId, thematicId]);

    useEffect(() => {
        if (errorTalks) {
            setErrorMessage('Une erreur est survenue lors de la récupération des causeries');
            return;
        }

        if (!talks || !talkId) return;
        const currentTalk = talks.find((talk) => talk.id === parseInt(talkId));

        if (!currentTalk) {
            setErrorMessage('Causerie non trouvée');
            return;
        }

        setPageTitle(currentTalk.title);
        setFiles(currentTalk.files.map((file) => ({ ...file, consulted: false })));
        setVideos(currentTalk.talk_videos.map((video) => ({ ...video, consulted: false })));
    }, [talks, talkId, errorTalks]);

    return {
        thematicId,
        talkId,
        pageTitle,
        files,
        videos,
        isLoading: isLoadingTalks,
        errorMessage,
        acknowledgeFile,
        acknowledgeVideo,
        areFilesAcknowledged,
        isEveryAssetAcknowledged
    };
};
